import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { UserDeck } from '@/types/deck';
import { CompactDeckCard } from './compact-deck-card.js';

interface DeckGridProps {
  readonly decks: readonly UserDeck[];
  readonly viewMode: 'grid' | 'list';
  readonly onView: (id: number) => void;
  readonly onSetDefault: (id: number) => void;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 100, damping: 12 },
  },
};

export function DeckGrid({ 
  decks, 
  viewMode, 
  onView, 
  onSetDefault 
}: Readonly<DeckGridProps>) {
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className={`grid gap-3 ${
        viewMode === "grid" 
          ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
          : "grid-cols-1"
      }`}
    >
      {decks.map((userDeck) => (
        <motion.div key={userDeck.id} variants={itemVariants}>
          {viewMode === "grid" ? (
            <CompactDeckCard 
              userDeck={userDeck}
              onView={() => onView(userDeck.deck.id)}
              onSetDefault={() => onSetDefault(userDeck.deck.id)}
            />
          ) : (
            <div className="bg-black/80 hover:bg-black/90 rounded-lg overflow-hidden">
              <div className="flex items-center p-3">
                {/* Card preview */}
                <div className="w-16 h-20 mr-3 flex-shrink-0">
                  <img 
                    src={`${userDeck.deck.deckUrl}/B13.svg`}
                    alt={userDeck.deck.name}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).src = `${userDeck.deck.deckUrl}/back.svg`;
                    }}
                  />
                </div>
                
                {/* Deck info */}
                <div className="flex-grow">
                  <div className="flex items-center">
                    <h3 className="font-bold">{userDeck.deck.name}</h3>
                    {userDeck.isDefault && (
                      <Badge className="ml-2 bg-amber-900/70 text-amber-300 border-amber-700 text-xs">
                        <Star className="h-3 w-3 fill-amber-300 mr-1" />
                        Default
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex gap-2 mt-1">
                    <Badge className="text-xs bg-black/20 capitalize">
                      {userDeck.deck.deckType ?? 'standard'}
                    </Badge>
                    
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex flex-col items-end gap-2">
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-2 py-1 h-auto"
                    onClick={() => onView(userDeck.deck.id)}
                  >
                    View
                  </Button>
                  
                  {!userDeck.isDefault && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-500/30 bg-transparent hover:bg-amber-500/10 text-amber-400 text-xs px-2 py-1 h-auto"
                      onClick={() => onSetDefault(userDeck.deck.id)}
                    >
                      Set Default
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      ))}
    </motion.div>
  );
}